import React from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { SessionPeek } from './SessionVideo'
import { pastLessonsCount } from '../../../utils/planFeatureLabel'

export type ComparePlanKey = 'pro' | 'standard' | 'weekly'

export interface ComparePlan {
  key: ComparePlanKey
  name: string
  features?: { label?: string; is_active?: boolean }[]
}

type Cell = boolean | string

interface CompareRow {
  id: string
  /** Translation keys, kept whole so the key check can see them. */
  name: string
  detail: string
  /** Offers a peek at the real 1-on-1 session clip from the row. */
  showsSession?: boolean
  cells: (history: Record<ComparePlanKey, string>, t: TFunction) => Record<ComparePlanKey, Cell>
}

/**
 * Every claim here is something the product does today:
 * - Pro gets ONE teacher correction a day, and the writing and the recording for that lesson are
 *   the same correction (utils/teacherCorrection.ts). Standard and Weekly can write and record,
 *   and their work is saved, but no teacher reviews it (write-speak/logic.ts, "locked").
 * - Teachers return a marked-up text with notes and a recording of their own
 *   (Corrections/CorrectionCard.tsx: Changes / Yours / Corrected, "Teacher's recording").
 * - The welcome call is booked on the post-payment page (PaymentSuccessful.tsx, Trafft).
 * - "Usually within 24 hours" and the apps sharing the plan, streak and words are promises the
 *   onboarding plan page already makes (TrialOfferRedesigned.tsx).
 */
const GROUPS: { id: 'lesson' | 'teacher'; title: string; rows: CompareRow[] }[] = [
  {
    id: 'lesson',
    title: 'plan:compare.groups.lesson',
    rows: [
      {
        id: 'newLesson',
        name: 'plan:compare.rows.newLesson.name',
        detail: 'plan:compare.rows.newLesson.detail',
        cells: (_, t) => ({
          pro: t('compare.cells.daily'),
          standard: t('compare.cells.daily'),
          weekly: t('compare.cells.weekly'),
        }),
      },
      {
        id: 'nativeAudio',
        name: 'plan:compare.rows.nativeAudio.name',
        detail: 'plan:compare.rows.nativeAudio.detail',
        cells: () => ({ pro: true, standard: true, weekly: true }),
      },
      {
        id: 'tapWord',
        name: 'plan:compare.rows.tapWord.name',
        detail: 'plan:compare.rows.tapWord.detail',
        cells: () => ({ pro: true, standard: true, weekly: true }),
      },
      {
        id: 'quiz',
        name: 'plan:compare.rows.quiz.name',
        detail: 'plan:compare.rows.quiz.detail',
        cells: () => ({ pro: true, standard: true, weekly: true }),
      },
      {
        id: 'writeSpeak',
        name: 'plan:compare.rows.writeSpeak.name',
        detail: 'plan:compare.rows.writeSpeak.detail',
        cells: (_, t) => ({
          pro: t('compare.cells.corrected'),
          standard: t('compare.cells.saved'),
          weekly: t('compare.cells.saved'),
        }),
      },
      {
        id: 'history',
        name: 'plan:compare.rows.history.name',
        detail: 'plan:compare.rows.history.detail',
        cells: (history) => history,
      },
      {
        id: 'apps',
        name: 'plan:compare.rows.apps.name',
        detail: 'plan:compare.rows.apps.detail',
        cells: () => ({ pro: true, standard: true, weekly: true }),
      },
    ],
  },
  {
    id: 'teacher',
    title: 'plan:compare.groups.teacher',
    rows: [
      {
        id: 'writingCorrection',
        name: 'plan:compare.rows.writingCorrection.name',
        detail: 'plan:compare.rows.writingCorrection.detail',
        cells: () => ({ pro: true, standard: false, weekly: false }),
      },
      {
        id: 'speakingCorrection',
        name: 'plan:compare.rows.speakingCorrection.name',
        detail: 'plan:compare.rows.speakingCorrection.detail',
        cells: () => ({ pro: true, standard: false, weekly: false }),
      },
      {
        id: 'welcomeCall',
        name: 'plan:compare.rows.welcomeCall.name',
        detail: 'plan:compare.rows.welcomeCall.detail',
        showsSession: true,
        cells: () => ({ pro: true, standard: false, weekly: false }),
      },
      {
        id: 'review',
        name: 'plan:compare.rows.review.name',
        detail: 'plan:compare.rows.review.detail',
        showsSession: true,
        cells: () => ({ pro: true, standard: false, weekly: false }),
      },
    ],
  },
]

// Plans keep "Your last N studied lessons" in their Stripe features; read N from there.
const fallbackHistory = (t: TFunction): Record<ComparePlanKey, string> => ({
  pro: t('compare.cells.lastLessons', { count: 30 }),
  standard: t('compare.cells.lastLessons', { count: 5 }),
  weekly: t('compare.cells.thisWeek'),
})

const historyFor = (plan: ComparePlan, t: TFunction): string => {
  for (const feature of plan.features || []) {
    if (!feature?.is_active) continue
    const count = pastLessonsCount(feature.label)
    if (count !== null) return t('compare.cells.lastLessons', { count })
  }
  return fallbackHistory(t)[plan.key]
}

const Tick = () => {
  const { t } = useTranslation('plan')
  return (
    <>
      <svg className='plan-compare-tick' width='20' height='20' viewBox='0 0 20 20' aria-hidden='true'>
        <path
          d='M4.5 10.5l3.5 3.5 7.5-8'
          fill='none'
          stroke='currentColor'
          strokeWidth='2.2'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
      <span className='visually-hidden'>{t('compare.included')}</span>
    </>
  )
}

const Missing = () => {
  const { t } = useTranslation('plan')
  return (
    <>
      <span className='plan-compare-missing' aria-hidden='true' />
      <span className='visually-hidden'>{t('compare.notIncluded')}</span>
    </>
  )
}

/**
 * One row per feature, one column per plan, laid on the same grid as the cards above it, so on
 * a wide screen each tick sits directly under the plan it belongs to. The rows where a plan has
 * nothing ARE the difference between the plans.
 */
const PlanCompare = ({ plans }: { plans: ComparePlan[] }) => {
  const { t } = useTranslation('plan')
  const history = plans.reduce((acc, plan) => ({ ...acc, [plan.key]: historyFor(plan, t) }), { ...fallbackHistory(t) })
  const style = { '--plan-count': plans.length } as React.CSSProperties

  return (
    <div className='plan-compare' role='table' aria-labelledby='plan-compare-title' style={style}>
      <div className='plan-compare-row plan-compare-row--head' role='row'>
        <div className='plan-compare-label' role='columnheader'>
          <h2 className='plan-section-title' id='plan-compare-title'>
            {t('compare.title')}
          </h2>
        </div>
        {plans.map((plan) => (
          <div className='plan-compare-cell plan-compare-plan' role='columnheader' key={plan.key}>
            {plan.name}
          </div>
        ))}
      </div>

      {GROUPS.map((group) => (
        <div className='plan-compare-group' role='rowgroup' key={group.id}>
          <div className='plan-compare-row plan-compare-row--group' role='row'>
            <h3 className='plan-compare-group-title' role='rowheader'>
              {t(group.title)}
            </h3>
            {/* Told before purchase, next to the corrections and calls it applies to. */}
            {group.id === 'teacher' && <p className='plan-compare-group-note'>{t('common:teacherLanguageNotice')}</p>}
          </div>
          {group.rows.map((row) => {
            const cells = row.cells(history, t)
            return (
              <div className='plan-compare-row' role='row' key={row.id}>
                <div className='plan-compare-label' role='rowheader'>
                  <span className='plan-compare-name'>{t(row.name)}</span>
                  <span className='plan-compare-detail'>{t(row.detail)}</span>
                  {row.showsSession && <SessionPeek />}
                </div>
                {plans.map((plan) => {
                  const cell = cells[plan.key]
                  // Green marks a value only where this plan gives more than the others.
                  const differs = typeof cell === 'string' && plans.some((other) => cells[other.key] !== cell)
                  return (
                    <div className='plan-compare-cell' role='cell' key={plan.key} data-plan={plan.key}>
                      {cell === true ? (
                        <Tick />
                      ) : cell === false ? (
                        <Missing />
                      ) : (
                        <span
                          className={`plan-compare-value${differs && plan.key === 'pro' ? ' plan-compare-value--more' : ''}`}
                        >
                          {cell}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

export default PlanCompare
